import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiClient';
import { S3_JOBS_PATH } from '../../constants/apiEndpoints';
import { fetchJobsBySourceId } from '../../services/jobsService';
import { addErrorNotification, addSuccessNotification } from './notificationSlice';

const JOB_LIST_LIMIT = 50;

export const fetchJobs = createAsyncThunk(
    's3Jobs/fetchJobs',
    async ({ sourceId, limit = JOB_LIST_LIMIT }, { dispatch, rejectWithValue }) => {
        if (!sourceId) {
            return rejectWithValue('Source id is missing for this source.');
        }

        try {
            return await fetchJobsBySourceId(sourceId, limit);
        } catch (error) {
            const message = error.message || 'Failed to load jobs for this source.';
            dispatch(addErrorNotification({ message }));
            return rejectWithValue(message);
        }
    }
);

export const createJob = createAsyncThunk(
    's3Jobs/createJob',
    async ({ payload, sourceId, limit = JOB_LIST_LIMIT }, { dispatch, rejectWithValue }) => {
        try {
            await apiFetch(S3_JOBS_PATH, {
                method: 'POST',
                body: JSON.stringify(payload),
                contentType: 'application/json',
                retry: false
            });
            dispatch(addSuccessNotification({
                message: `Job created successfully for source "${sourceId}".`
            }));
            return await fetchJobsBySourceId(sourceId, limit);
        } catch (error) {
            const message = error.message || 'Failed to create job. Please try again.';
            dispatch(addErrorNotification({ message }));
            return rejectWithValue(message);
        }
    }
);

const initialState = {
    jobs: [],
    status: 'idle',
    error: null,
    isCreating: false,
};

const s3JobsSlice = createSlice({
    name: 's3Jobs',
    initialState,
    reducers: {
        clearJobs: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchJobs.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchJobs.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.jobs = action.payload.jobs;
            })
            .addCase(fetchJobs.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
                state.jobs = [];
            })
            .addCase(createJob.pending, (state) => {
                state.isCreating = true;
            })
            .addCase(createJob.fulfilled, (state, action) => {
                state.isCreating = false;
                state.jobs = action.payload.jobs;
            })
            .addCase(createJob.rejected, (state) => {
                state.isCreating = false;
            });
    },
});

export const { clearJobs } = s3JobsSlice.actions;
export const selectJobs = (state) => state.s3Jobs.jobs;
export const selectJobsStatus = (state) => state.s3Jobs.status;
export const selectJobsError = (state) => state.s3Jobs.error;
export const selectIsCreatingJob = (state) => state.s3Jobs.isCreating;

export default s3JobsSlice.reducer;
